import { Linking, Share } from "react-native";
import { os } from "../platform/";
import AppStore from "../storage/appStore";
import STRINGS from "../constants/strings";

/**
 * Opens the device's map application with directions to the specified latitude and longitude.
 *
 * @param {number|string} latitude - The latitude of the destination. Can be a number or a string representation of a number.
 * @param {number|string} longitude - The longitude of the destination. Can be a number or a string representation of a number.
 *
 * @returns {void}
 *
 * @throws Will log an error if latitude or longitude are invalid.
 *
 * @example
 * openDirections(37.7749, -122.4194); // Opens map app to San Francisco coordinates
 */
export const openDirections = (latitude, longitude) => {
  const { setErrorMessage } = AppStore.getState();

  latitude = parseFloat(latitude); // Convert to a float
  longitude = parseFloat(longitude); // Remove any non-numeric characters

  if (!isNaN(latitude) && !isNaN(longitude) && latitude !== null && longitude !== null) {
    const url = (() => {
      switch (os) {
        case "ios":
          return `maps:0,0?q=Location@${latitude},${longitude}`;
        case "android":
          return `geo:0,0?q=${latitude},${longitude}(Location)`;
        default:
          return googleMapLink(latitude, longitude);
      }
    })();

    Linking.openURL(url).catch((err) => console.error("Failed to open map", err));
  } else {
    setErrorMessage(STRINGS.mapCouldNotMeOpened);
    console.error("Invalid latitude or longitude");
  }
};

/**
 * Generates a Google Maps link for driving directions to the specified latitude and longitude.
 *
 * @param {number} latitude - The latitude of the destination.
 * @param {number} longitude - The longitude of the destination.
 * @returns {string} The Google Maps link for driving directions.
 */
const googleMapLink = (latitude, longitude) => {
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
};

/**
 * Shares a message with a given subject using the Share API.
 *
 * @param {string} msg - The message to be shared.
 * @param {string} segment_name - The subject of the message.
 * @returns {Promise<void>} A promise that resolves when the share action is complete.
 */
export const share = async (msg, segment_name) => {
  await Share.share({ message: msg }, { subject: segment_name }).catch(() => console.log("Share cancelled"));
};

/**
 * Attempts to open the provided URL using the device's default browser.
 * Logs an error message if the operation fails.
 *
 * @async
 * @function handleLinkingPress
 * @param {string} url - The URL to open.
 * @returns {Promise<void>} Resolves when the URL is opened or logs an error if it fails.
 */
export const handleLinkingPress = async (url) => {
  try {
    await Linking.openURL(url);
  } catch (error) {
    console.error("Failed to open website:", error);
  }
};

/**
 * Sanitize and validate latitude/longitude
 * @param {number|string} value - The coordinate value to sanitize.
 */
export const sanitizeCoordinate = (value) => {
  if (typeof value === "string") {
    return parseFloat(value.replace(/'/g, ""));
  } else if (typeof value === "number") {
    return value; // Already a valid number
  }
  return null; // Return null for invalid values
};

/**
 * Returns the appropriate marker icon image for a given parking type and state.
 * @param {string} type - The type of parking location (e.g., "meter", "structure").
 * @param {"enabled" | "disabled"} state - The marker state.
 * @returns {*} The required image asset for the marker icon.
 */
export const getMarkerIcon = (type = "", state = "disabled") => {
  const icons = {
    default:
      os === "android"
        ? {
            enabled: { uri: "parkiconenabled" },
            disabled: { uri: "parkicondisabled" },
          }
        : {
            enabled: require("../assets/images/park-icon-enabled.png"),
            disabled: require("../assets/images/park-icon-disabled.png"),
          },
  };

  return (icons[type] || icons.default)[state];
};

/**
 * Validates if coordinates are within valid lat/lng ranges
 * @param {number} lat - The latitude to validate.
 * @param {number} lng - The longitude to validate.
 * @returns {boolean} True if coordinates are valid, false otherwise.
 */
export const isValidCoordinate = (lat, lng) => {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};
